import { Button, Modal, ButtonGroup, Alert } from "react-bootstrap";
import axios from "axios";
import DataTable from "~/shared/DataTable";
import { useReducer, useState } from "react";
import FormSection from "~/shared/form/FormSection";
import FieldContext from "~/shared/form/FieldContext";
import ControlledForm from "~/shared/form/ControlledForm";
import { AddressFields, EmergencyContactFields, FullNameFields } from "../family/FieldGroups";
import TextInput from "~/shared/form/TextInput";
import useAxiosData from "~/shared/use-axios-data";
import FormButton from "~/shared/form/FormButton";
import FormResponse from "~/shared/form/FormResponse";
import { getFullName, localeFormatDate } from "~/shared/utils";
import MultiSelectInput from "~/shared/form/MultiSelectInput";
import useModal from "~/shared/UseModal";
import SelectInput from "~/shared/form/SelectInput";
import { CERTIFICATION_TYPES } from "../staff/ManageCertifications";
import { useForm } from "react-hook-form";
import CheckboxInput from "~/shared/form/CheckboxInput";
import FileView from "~/shared/form/FileView";
import AlertBox from "~/shared/AlertBox";

export default function ManageStaffModal({ show, onHide, staff }) {
    const [currentTab, setCurrentTab] = useState("Account Information");
    const [programs] = useAxiosData(() => axios.get("programs"), []);
    const [editing, setEditing] = useState(false);
    const [, forceUpdate] = useReducer(n => n + 1, 0);
    const [showCertificationModal, setShowCertificationModal] = useState(false);
    const { ModalDialog, showConfirm } = useModal();
    const formHook = useForm({ disabled: true });

    function formatTime(time) {
        return new Date(`1/1/00 ${time}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric" });
    }

    function onSubmit(data) {
        data.id = staff._id;
        data.programs = data.programs.map(name => programs.find(p => p.name === name)._id);
    }

    function onSuccess(_, form, data) {
        data.programs = data.programs.map(id => programs.find(p => p._id === id).name);
        form.reset(data);
        form.reset();
        Object.keys(data).filter(key => staff[key] !== undefined).forEach(key => staff[key] = data[key]);
        setEditing(false);
    }

    function getDocumentActions({ _id, name }) {
        return <>
            <DataTable.Action onClick={() => openDocument(_id)}>View</DataTable.Action>
            <DataTable.Action onClick={(_, context) => deleteDocument(_id, name, context)} manualSpinner>Delete</DataTable.Action>
        </>;
    }

    async function openDocument(_id) {
        const response = await axios.get(`admin/staff/documents/get/${_id}`);
        window.open(response.data.data, "_blank");
    }

    async function deleteDocument(_id, name, context) {
        if (await showConfirm("Delete Document", `Are you sure you want to permanently delete the document '${name}'?`)) {
            context.setLoading(true);
            await axios.post(`admin/staff/documents/delete/${_id}`, { id: staff._id });
            staff.documents = staff.documents.filter(d => d._id !== _id);
            forceUpdate();
        }
    }

    function getStatusVerb(status) {
        return {
            "Approved": "Approve",
            "Rejected": "Reject",
            "Submitted": "Submit"
        }[status] ?? "";
    }

    function getCertificationActions(certification) {
        return <>
            <DataTable.Action onClick={() => viewCertification(certification)}>View</DataTable.Action>
            {certification.status === "Submitted" ? <>
                <DataTable.Action onClick={(_, context) => updateCertificationStatus(certification._id, "Approved", "Approve", context)} manualSpinner>Approve</DataTable.Action>
                <DataTable.Action onClick={(_, context) => updateCertificationStatus(certification._id, "Rejected", "Reject", context)} manualSpinner>Reject</DataTable.Action>
            </> : certification.status === "Expired" ? (
                <DataTable.Action onClick={(_, context) => deleteCertification(certification._id, context)} manualSpinner>Delete</DataTable.Action>
            ) : (
                <DataTable.Action onClick={(_, context) => updateCertificationStatus(certification._id, "Submitted", `Un${getStatusVerb(certification.status).toLowerCase()}`, context)} manualSpinner>
                    Un{getStatusVerb(certification.status).toLowerCase()}
                </DataTable.Action>
            )}
        </>;
    }

    function viewCertification(certification) {
        setShowCertificationModal(true);

        if (!CERTIFICATION_TYPES.includes(certification.type)) {
            formHook.setValue("type", "Other");
            formHook.setValue("typeOther", certification.type);
        } else {
            formHook.setValue("type", certification.type);
        }

        formHook.setValue("dateReceived", certification.dateReceived.split('T')[0]);
        formHook.setValue("expirationDate", certification.expirationDate.split('T')[0]);
        formHook.setValue("document", certification.document);

    }

    async function updateCertificationStatus(_id, status, action, context) {
        if (!await showConfirm(`${action} Certification`, `Are you sure you want to ${action.toLowerCase()} this certification?`)) {
            return;
        }

        context.setLoading(true);
        await axios.post(`admin/staff/certifications/update/${_id}`, { id: staff._id, status });
        staff.certifications.find(c => c._id === _id).status = status;
        forceUpdate();
    }

    async function deleteCertification(_id, context) {
        if (!await showConfirm(`Delete Certification`, `Are you sure you want to delete this certification?`)) {
            return;
        }

        context.setLoading(true);
        await axios.post(`admin/staff/certifications/delete/${_id}`, { id: staff._id });
        staff.certifications = staff.certifications.filter(c => c._id !== _id);
        forceUpdate();
    }

    function TabButton({ title = "", children = title }) {
        return (
            <Button variant="light" className="d-flex justify-content-center align-items-center gap-2" onClick={() => (setCurrentTab(title), setEditing(false))} active={currentTab === title}>
                {children}
            </Button>
        );
    }

    return (
        <Modal show={show} onHide={onHide} className="large-modal fixed-size-modal manage-staff-modal">
            {ModalDialog}
            <Modal.Header closeButton>
                <Modal.Title>View Staff: {getFullName(staff?.details)}</Modal.Title>
            </Modal.Header>
            {staff && <Modal.Body className="overflow-auto" style={{ maxHeight: "39rem" }}>
                <ButtonGroup className="w-full mb-2 overflow-auto">
                    <TabButton title="Account Information" />
                    <TabButton title="Availability" />
                    <TabButton title="Certifications">Certifications {staff.certifications?.some(c => c.status === "Submitted") && <i className="bi bi-circle-fill fs-6" style={{ color: currentTab === "Certifications" ? "white" : "var(--ngf-red)" }} />}</TabButton>
                    <TabButton title="Documents" />
                </ButtonGroup>

                {currentTab === "Account Information" ? (
                    <ControlledForm options={{ defaultValues: { ...staff, programs: staff.programs.map(id => programs.find(p => p._id === id)?.name ?? id) }, disabled: !editing }} onSubmit={onSubmit} post="admin/staff/update" onSuccess={onSuccess}>
                        <FieldContext id="details">
                            <div className="flex flex-col gap-3">
                                <FormSection row wrap gap={1}>
                                    <FullNameFields />
                                </FormSection>
                                <FormSection row wrap gap={1}>
                                    <AddressFields />
                                </FormSection>
                                <FormSection row wrap gap={1}>
                                    <TextInput label="Email" absoluteId="email" preset="email" required />
                                    <MultiSelectInput label="Programs" absoluteId="programs" options={programs.map(p => p.name)} placeholder="Select programs..." required />
                                </FormSection>
                                <FormSection row wrap gap={1} title="Emergency Contact" heading>
                                    <EmergencyContactFields />
                                </FormSection>
                            </div>
                        </FieldContext>
                        <FormResponse />
                        {editing ? <div className="btn-container mt-4">
                            <FormButton legacy submit>Save</FormButton>
                            <FormButton legacy reset onClick={() => setEditing(false)}>Cancel</FormButton>
                        </div> : (
                            <FormButton legacy onClick={() => setEditing(true)} className="mt-4">Edit</FormButton>
                        )}
                    </ControlledForm>
                ) : currentTab === "Availability" ? (
                    <DataTable data={staff.availability.map(a => a.times.map(t => ({ ...a, ...t }))).flat()}>
                        <DataTable.Column id="day">Day</DataTable.Column>
                        <DataTable.Column id="start" format={formatTime}>Start</DataTable.Column>
                        <DataTable.Column id="end" format={formatTime}>End</DataTable.Column>
                    </DataTable>
                ) : currentTab === "Certifications" ? (
                    <DataTable data={staff.certifications}>
                        <DataTable.Column id="type" sortable>Certification Type</DataTable.Column>
                        <DataTable.Column id="dateReceived" format={localeFormatDate} sortable>Date</DataTable.Column>
                        <DataTable.Column id="expirationDate" format={localeFormatDate} sortable>Expires On</DataTable.Column>
                        <DataTable.Column id="status" sortable>Status</DataTable.Column>
                        <DataTable.Column value={getCertificationActions}>Actions</DataTable.Column>
                        <DataTable.Message condition={staff.certifications.length === 0}>
                            <AlertBox type="info">No certifications found.</AlertBox>
                        </DataTable.Message>
                    </DataTable>
                ) : (
                    <DataTable data={staff.documents}>
                        <DataTable.Column id="type" sortable>Type</DataTable.Column>
                        <DataTable.Column id="name" className="text-truncate" style={{ maxWidth: "20rem" }} sortable>Name</DataTable.Column>
                        <DataTable.Column value={getDocumentActions}>Actions</DataTable.Column>
                        <DataTable.Message condition={staff.documents.length === 0}>
                            <AlertBox type="info">No documents found.</AlertBox>
                        </DataTable.Message>
                    </DataTable>
                )}
            </Modal.Body>}
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>

            <Modal show={showCertificationModal} onHide={() => setShowCertificationModal(false)} className="fixed-size-modal">
                <Modal.Header closeButton>
                    <Modal.Title>View Certification</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ControlledForm hook={formHook} className="flex flex-col gap-3">
                        <FormSection row wrap gap={1}>
                            <SelectInput label="Type" id="type" options={[...CERTIFICATION_TYPES, "Other"]} maxWidth={formHook.watch("type") === "Other" ? 3 : undefined} placeholder="Select type" required />
                            {formHook.watch("type") === "Other" && <TextInput label="Certification Type" id="typeOther" required />}
                        </FormSection>
                        <FormSection row wrap gap={1}>
                            <TextInput label="Date Received" id="dateReceived" type="date" required />
                            <TextInput label="Expiration Date" id="expirationDate" type="date" required validate={v => new Date(`${v}T23:59:59`) > new Date()} invalid="Expiration date must be in the future." />
                        </FormSection>
                        <FileView label="Certification Document" id="document" get="admin/staff/certifications/documents/get" />
                        <CheckboxInput id="completed" label="Certification Completed" boldLabel />
                    </ControlledForm>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCertificationModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </Modal>
    );
}